<template>
  <button
    class="emh-ui-select-button"
    :class="{ '-selected': selected, '-disabled': disabled, '-with-image': imageUrl }"
    :aria-label="label"
    :disabled="disabled"
    @click="selectOption"
  >
    <wb-icon
      v-if="selected"
      class="emh-ui-select-button__select-icon"
      :name.attr="'bds/checkmark-circle/16'"
      data-test-id="emh-ui-select-button__select-icon"
    />
    <div v-if="imageUrl" class="emh-ui-select-button__image">
      <img :src="imageUrl" :alt="label" data-test-id="emh-ui-select-button__image" />
    </div>
    <span class="emh-ui-select-button__label">
      {{ label }}
      <template v-if="count">({{ count }})</template>
    </span>
  </button>
</template>

<script lang="ts">
export default { name: 'UISelectButton' };
</script>

<script lang="ts" setup>
const props = defineProps({
  count: { type: [String, Number], default: null },
  disabled: { type: Boolean, default: false },
  imageUrl: { type: String, default: '' },
  label: { type: String, required: true },
  selected: { type: Boolean, required: true },
  value: { type: [String, Number], required: true },
});

const emit = defineEmits(['select']);

const selectOption = () => emit('select', props);
</script>

<style lang="scss" scoped>
.emh-ui-select-button {
  @include wb-type-label;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  color: var(--wb-grey-20);
  background: var(--wb-white);
  border: 1px solid var(--wb-grey-75);
  border-radius: 4px;
  text-align: center;
  cursor: pointer;
  min-height: 50px;
  padding: 1rem 1.5rem;

  @include breakpoint-from(mq4) {
    padding: 0.875rem 1rem;
  }

  @include breakpoint-from(mq5) {
    padding: 1rem 1.125rem;
  }

  @include breakpoint-from(mq6) {
    padding: 1rem 1.5rem;
  }

  &:not(&.-disabled):hover {
    color: var(--wb-black);
    border-color: var(--wb-grey-70);
    background-color: var(--wb-grey-90);
    transition: background-color 0.3s ease-in-out;
  }

  &.-selected {
    background-color: var(--wb-grey-90);
    border-color: var(--wb-blue-45);
  }

  &.-disabled {
    opacity: 0.35;
    cursor: default;
  }

  &.-with-image {
    line-height: 1.5em;
  }

  &__select-icon {
    --size: 16px;

    position: absolute;
    top: 6px;
    right: 6px;
    color: var(--wb-blue-45);
  }

  &__label {
    display: flex;
    gap: 6px;
    line-height: 1.5rem;
    justify-content: center;
    align-items: center;
  }

  &__image {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 1.5em;
    width: 100%;

    & > img {
      height: auto;
      max-width: 85%;

      @include breakpoint-from(mq4) {
        max-width: 100%;
      }

      @include breakpoint-from(mq5) {
        max-width: 90%;
      }

      @include breakpoint-from(mq6) {
        max-width: 85%;
      }
    }
  }
}
</style>
